import { gql } from "@apollo/client";
import { client } from "../apolloClient";
import { useQuery, useMutation } from "@apollo/client";

/**
 * useProjectResorceMappingApi custom hook for project resource mapping operations.
 * - Provides CRUD operations for project resource mappings using Apollo Client and GraphQL.
 * - Handles cache updates for project resource mapping queries and mutations.
 * - Exposes helpers for reading/writing/updating project resource mappings in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting resource mappings.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 * - Supports fetching resources by project, updating allocations, and more.
 */

// GraphQL Queries and Mutations
const PROJECT_RESOURCE_QUERIES = {
  // Query to fetch all project resource mappings
  GET_PROJECT_RESOURCES: gql`
    query GetProjectResourceMappings {
      GetProjectResourceMappings {
        project_resource_mapping_id
        project_id
        resource_id
        role_id
        start_date
        end_date
        billability
        allocation
        is_active
        is_primary
        is_project_manager
        is_project_owner
        created_on
        updated_on
        resource {
          resource_id
          username
          email
          mobile
          designation
          first_name
          last_name
          previous_year_of_experience
          date_of_joining
          type_of_service
          business_unit
          reporting_manager
          availability
          country_id
          employee_code
          department
          is_active
          created_on
          updated_on
          role_id
        }
      }
    }
  `,
  // Query to fetch resource mappings by project ID
  GET_PROJECT_RESOURCES_BY_PROJECT_ID: gql`
    query GetProjectResourceMappingsByProjectId($projectId: Int!) {
      GetProjectResourceMappingsByProjectId(project_id: $projectId) {
        project_resource_mapping_id
        project_id
        resource_id
        role_id
        start_date
        end_date
        billability
        allocation
        is_active
        is_primary
        is_project_manager
        is_project_owner
        created_on
        updated_on
        resource {
          resource_id
          username
          email
          mobile
          designation
          first_name
          last_name
          previous_year_of_experience
          date_of_joining
          type_of_service
          business_unit
          reporting_manager
          availability
          country_id
          employee_code
          department
          is_active
          created_on
          updated_on
          role_id
        }
      }
    }
  `,
  GET_RESOURCES_BY_PROJECT_ID: gql`
    query GetResourcesByProjectId($projectId: Int! $projectManagerOnly: Boolean) {
      GetResourcesByProjectId(project_id: $projectId project_manager_only: $projectManagerOnly) {
        resource_id
        username
        email
        mobile
        designation
        first_name
        last_name
        previous_year_of_experience
        date_of_joining
        type_of_service
        business_unit
        reporting_manager
        availability
        country_id
        employee_code
        department
        is_active
        created_on
        updated_on
        role_id
      }
    }
  `,
  GET_RESOURCES_INCLUDING_PO_AND_PM_BY_PROJECT_ID: gql`
    query GetResourcesIncludingPOAndPMByProjectId(
      $projectId: Int!
      $projectManagerOnly: Boolean
      $departments: [String]
    ) {
      GetResourcesIncludingPOAndPMByProjectId(
        project_id: $projectId
        project_manager_only: $projectManagerOnly
        departments: $departments
      ) {
        resource_id
        username
        email
        mobile
        designation
        first_name
        last_name
        previous_year_of_experience
        date_of_joining
        type_of_service
        business_unit
        reporting_manager
        availability
        country_id
        employee_code
        department
        is_active
        created_on
        updated_on
        role_id
      }
    }
  `,
  // Mutation to add a new project resource mapping
  ADD_PROJECT_RESOURCE: gql`
    mutation AddProjectResourceMapping(
      $projectId: Int
      $resourceId: Int
      $roleId: Int
      $startDate: String
      $endDate: String
      $billability: Boolean
      $allocation: String
    ) {
      AddProjectResourceMapping(
        project_id: $projectId
        resource_id: $resourceId
        role_id: $roleId
        start_date: $startDate
        end_date: $endDate
        billability: $billability
        allocation: $allocation
      ) {
        project_resource_mapping_id
        project_id
        resource_id
        role_id
        start_date
        end_date
        billability
        allocation
        is_active
        created_on
        updated_on
        resource {
          resource_id
          username
          email
          mobile
          designation
          first_name
          last_name
          previous_year_of_experience
          date_of_joining
          type_of_service
          business_unit
          reporting_manager
          availability
          country_id
          employee_code
          department
          is_active
          created_on
          updated_on
          role_id
        }
      }
    }
  `,
  // Mutation to update a project resource mapping
  UPDATE_PROJECT_RESOURCE: gql`
    mutation UpdateProjectResourceMapping(
      $projectResourceMappingId: Int!
      $projectId: Int
      $resourceId: Int
      $roleId: Int
      $startDate: String
      $endDate: String
      $billability: Boolean
      $allocation: String
    ) {
      UpdateProjectResourceMapping(
        project_resource_mapping_id: $projectResourceMappingId
        project_id: $projectId
        resource_id: $resourceId
        role_id: $roleId
        start_date: $startDate
        end_date: $endDate
        billability: $billability
        allocation: $allocation
      ) {
        project_resource_mapping_id
        project_id
        resource_id
        role_id
        start_date
        end_date
        billability
        allocation
        is_active
        created_on
        updated_on
        resource {
          resource_id
          username
          email
          mobile
          designation
          first_name
          last_name
          previous_year_of_experience
          date_of_joining
          type_of_service
          business_unit
          reporting_manager
          availability
          country_id
          employee_code
          department
          is_active
          created_on
          updated_on
          role_id
        }
      }
    }
  `,
  // Mutation to delete a project resource mapping
  DELETE_PROJECT_RESOURCE: gql`
    mutation DeleteProjectResourceMapping($projectResourceMappingId: Int!) {
      DeleteProjectResourceMapping(
        project_resource_mapping_id: $projectResourceMappingId
      ) {
        project_resource_mapping_id
        project_id
        resource_id
        role_id
        start_date
        end_date
        billability
        allocation
        is_active
        created_on
        updated_on
      }
    }
  `,
};

/** Apollo variables for GetResourcesIncludingPOAndPMByProjectId — omit departments when PM-only. */
const variablesForIncludingPOAndPMQuery = (
  projectId,
  projectManagerOnly,
  departments = []
) => {
  const vars = {
    projectId: parseInt(projectId, 10),
    projectManagerOnly,
  };
  if (!projectManagerOnly) {
    const deptList = Array.isArray(departments)
      ? departments.map((d) => String(d).trim()).filter(Boolean)
      : [];
    vars.departments = deptList;
  }
  return vars;
};

// Cache operations for project resource mappings
const cacheOperations = {
  /**
   * Writes the provided resource mappings array to Apollo Client cache.
   * @param {Array} projectResorceMapping
   */
  writeProjectResorceMappings: (projectResorceMapping) => {
    if (!projectResorceMapping) return;
    client.writeQuery({
      query: PROJECT_RESOURCE_QUERIES.GET_PROJECT_RESOURCES,
      data: {
        GetProjectResourceMappings: projectResorceMapping,
      },
    });
  },

  /**
   * Reads the resource mappings array from Apollo Client cache.
   * @returns {Array} Array of resource mapping objects or empty array if not found.
   */
  readProjectResorceMappings: () => {
    try {
      const { GetProjectResourceMappings } =
        client.readQuery({
          query: PROJECT_RESOURCE_QUERIES.GET_PROJECT_RESOURCES,
        }) || {};
      return GetProjectResourceMappings || [];
    } catch {
      return [];
    }
  },

  /**
   * Updates a resource mapping in the Apollo Client cache.
   * @param {Object} updatedProjectResorceMapping
   */
  updateProjectResorceMappingsInCache: (updatedProjectResorceMapping) => {
    const existingProjectResorceMappings =
      cacheOperations.readProjectResorceMappings();
    const updatedProjectResorceMappings = existingProjectResorceMappings.map(
      (projectResource) =>
        projectResource.project_resource_mapping_id ===
        updatedProjectResorceMapping.project_resource_mapping_id
          ? updatedProjectResorceMapping
          : projectResource
    );
    cacheOperations.writeProjectResorceMappings(updatedProjectResorceMappings);
  },

  /**
   * Reads resource mappings by project ID from Apollo Client cache.
   * @param {number|string} projectId
   * @returns {Array}
   */
  readProjectResourcesByProjectId: (projectId) => {
    try {
      const { GetProjectResourceMappingsByProjectId = [] } =
        client.readQuery({
          query: PROJECT_RESOURCE_QUERIES.GET_PROJECT_RESOURCES_BY_PROJECT_ID,
          variables: { projectId: parseInt(projectId) },
        }) || {};
      return GetProjectResourceMappingsByProjectId;
    } catch {
      return [];
    }
  },

  /**
   * Writes resource mappings by project ID to Apollo Client cache.
   * @param {number|string} projectId
   * @param {Array} resources
   */
  writeProjectResourcesByProjectId: (projectId, resources) => {
    client.writeQuery({
      query: PROJECT_RESOURCE_QUERIES.GET_PROJECT_RESOURCES_BY_PROJECT_ID,
      variables: { projectId: parseInt(projectId) },
      data: { GetProjectResourceMappingsByProjectId: resources },
    });
  },

  readResourcesByProjectId: (projectId, projectManagerOnly = false) => {
    try {
      const { GetResourcesByProjectId = [] } =
        client.readQuery({
          query: PROJECT_RESOURCE_QUERIES.GET_RESOURCES_BY_PROJECT_ID,
          variables: {
            projectId: parseInt(projectId),
            projectManagerOnly,
          },
        }) || {};
      return GetResourcesByProjectId;
    } catch {
      return [];
    }
  },

  writeResourcesByProjectId: (projectId, resources, projectManagerOnly = false) => {
    client.writeQuery({
      query: PROJECT_RESOURCE_QUERIES.GET_RESOURCES_BY_PROJECT_ID,
      variables: {
        projectId: parseInt(projectId),
        projectManagerOnly,
      },
      data: { GetResourcesByProjectId: resources },
    });
  },

  readResourcesIncludingPOAndPMByProjectId: (
    projectId,
    projectManagerOnly = false,
    departments = []
  ) => {
    try {
      const { GetResourcesIncludingPOAndPMByProjectId = [] } =
        client.readQuery({
          query: PROJECT_RESOURCE_QUERIES.GET_RESOURCES_INCLUDING_PO_AND_PM_BY_PROJECT_ID,
          variables: variablesForIncludingPOAndPMQuery(
            projectId,
            projectManagerOnly,
            departments
          ),
        }) || {};
      return GetResourcesIncludingPOAndPMByProjectId;
    } catch {
      return [];
    }
  },

  writeResourcesIncludingPOAndPMByProjectId: (
    projectId,
    resources,
    projectManagerOnly = false,
    departments = []
  ) => {
    client.writeQuery({
      query: PROJECT_RESOURCE_QUERIES.GET_RESOURCES_INCLUDING_PO_AND_PM_BY_PROJECT_ID,
      variables: variablesForIncludingPOAndPMQuery(
        projectId,
        projectManagerOnly,
        departments
      ),
      data: { GetResourcesIncludingPOAndPMByProjectId: resources },
    });
  },
};

export const useProjectResorceMappingApi = () => {
  const [addProjectResorceMappingeMutation] = useMutation(
    PROJECT_RESOURCE_QUERIES.ADD_PROJECT_RESOURCE
  );
  const [updateProjectResorceMappingMutation] = useMutation(
    PROJECT_RESOURCE_QUERIES.UPDATE_PROJECT_RESOURCE
  );

  /**
   * Fetches all project resource mappings.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getProjectResorceMappings = async () => {
    try {
      const { data } = await client.query({
        query: PROJECT_RESOURCE_QUERIES.GET_PROJECT_RESOURCES,
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetProjectResourceMappings };
    } catch (error) {
      console.error("Error fetching GetProjectResourceMappings:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches resource mappings by project ID.
   * @param {number|string} projectId
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getProjectResorceMappingsByProjectId = async (projectId) => {
    try {
      const { data } = await client.query({
        query: PROJECT_RESOURCE_QUERIES.GET_PROJECT_RESOURCES_BY_PROJECT_ID,
        variables: { projectId: parseInt(projectId) },
        fetchPolicy: "network-only",
      });
      return {
        success: true,
        data: data.GetProjectResourceMappingsByProjectId,
      };
    } catch (error) {
      console.error("Error fetching GetProjectResourceMappings:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches a project resource mapping by its ID.
   * @param {number|string} project_resource_mapping_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getProjectResorceMappingById = async (project_resource_mapping_id) => {
    try {
      const { data } = await client.query({
        query: PROJECT_RESOURCE_QUERIES.GET_PROJECT_RESOURCE_BY_ID,
        variables: { project_resource_mapping_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetProjectResourceById };
    } catch (error) {
      console.error("Error fetching project resource:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Creates a new project resource mapping.
   * @param {Object} formData
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const createProjectResorceMapping = async (formData) => {
    try {
      const { data, errors } = await addProjectResorceMappingeMutation({
        variables: {
          resourceId: parseInt(formData.resource_id),
          projectId: parseInt(formData.project_id),
          roleId: parseInt(formData.role_id),
          startDate: formData.start_date,
          endDate: formData.end_date,
          billability: formData.billability,
          allocation: formData.allocation,
          isActive: formData.is_active,
        },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.AddProjectResourceMapping) {
        throw new Error("Failed to create role");
      }

      return {
        success: true,
        data: data.AddProjectResourceMapping,
      };
    } catch (error) {
      // Handle GraphQL errors
      if (error.graphQLErrors?.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        throw new Error(graphQLError.message);
      }

      // Handle network errors
      if (error.networkError?.result?.errors?.length > 0) {
        const networkError = error.networkError.result.errors[0];
        throw new Error(networkError.message);
      }

      throw error;
    }
  };

  /**
   * Updates an existing project resource mapping.
   * @param {number|string} projectResourceMappingId
   * @param {Object} formData
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateProjectResorceMapping = async (
    projectResourceMappingId,
    formData
  ) => {
    try {
      const { data, errors } = await updateProjectResorceMappingMutation({
        variables: {
          projectResourceMappingId: parseInt(projectResourceMappingId),
          resourceId: parseInt(formData.resource_id),
          projectId: parseInt(formData.project_id),
          roleId: parseInt(formData.role_id),
          startDate: formData.start_date,
          endDate: formData.end_date,
          billability: formData.billability,
          allocation: formData.allocation,
          isActive: formData.is_active,
        },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.UpdateProjectResourceMapping) {
        throw new Error("Failed to update ProjectResorceMapping");
      }

      return {
        success: true,
        data: data.UpdateProjectResourceMapping,
      };
    } catch (error) {
      // Handle GraphQL errors
      if (error.graphQLErrors?.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        throw new Error(graphQLError.message);
      }

      // Handle network errors
      if (error.networkError?.result?.errors?.length > 0) {
        const networkError = error.networkError.result.errors[0];
        throw new Error(networkError.message);
      }

      throw error;
    }
  };

  /**
   * Deletes a project resource mapping by its ID.
   * @param {number|string} projectResourceMappingId
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const deleteProjectResorceMapping = async (projectResourceMappingId) => {
    try {
      const { data } = await client.mutate({
        mutation: PROJECT_RESOURCE_QUERIES.DELETE_PROJECT_RESOURCE,
        variables: {
          projectResourceMappingId: parseInt(projectResourceMappingId),
        },
      });

      const existingProjectResorceMappings =
        cacheOperations.readProjectResorceMappings();
      cacheOperations.writeProjectResorceMappings(
        existingProjectResorceMappings.filter(
          (projectResorceMapping) =>
            projectResorceMapping.project_resource_mapping_id !==
            projectResourceMappingId
        )
      );

      return { success: true, data: data.DeleteProjectResourceMapping };
    } catch (error) {
      console.error("Error deleting ProjectResourceMapping:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches resource mappings by project ID and writes them to cache.
   * @param {number|string} projectId
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getProjectResourcesByProjectId = async (projectId) => {
    try {
      const { data } = await client.query({
        query: PROJECT_RESOURCE_QUERIES.GET_PROJECT_RESOURCES_BY_PROJECT_ID,
        variables: { projectId: parseInt(projectId) },
        fetchPolicy: "network-only",
      });

      cacheOperations.writeProjectResourcesByProjectId(
        projectId,
        data.GetProjectResourceMappingsByProjectId
      );

      return {
        success: true,
        data: {
          GetProjectResourceMappingsByProjectId:
            data.GetProjectResourceMappingsByProjectId,
        },
      };
    } catch (error) {
      console.error("Error fetching project resources by project ID:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches resources assigned to a project (flat Resource list).
   * @param {number|string} projectId
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getResourcesByProjectId = async (
    projectId,
    projectManagerOnly = false
  ) => {
    try {
      const { data } = await client.query({
        query: PROJECT_RESOURCE_QUERIES.GET_RESOURCES_BY_PROJECT_ID,
        variables: {
          projectId: parseInt(projectId),
          projectManagerOnly,
        },
        fetchPolicy: "network-only",
      });
      const resources = data?.GetResourcesByProjectId || [];
      cacheOperations.writeResourcesByProjectId(
        projectId,
        resources,
        projectManagerOnly
      );
      return { success: true, data: resources };
    } catch (error) {
      console.error("Error fetching GetResourcesByProjectId:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches resources (including PO & PM) assigned to a project (flat Resource list).
   * @param {number|string} projectId
   * @param {boolean} projectManagerOnly
   * @param {string[]} departments optional department name filters (resources only; ignored when projectManagerOnly is true)
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getResourcesIncludingPOAndPMByProjectId = async (
    projectId,
    projectManagerOnly = false,
    departments = []
  ) => {
    try {
      const variables = variablesForIncludingPOAndPMQuery(
        projectId,
        projectManagerOnly,
        departments
      );
      const { data } = await client.query({
        query: PROJECT_RESOURCE_QUERIES.GET_RESOURCES_INCLUDING_PO_AND_PM_BY_PROJECT_ID,
        variables,
        fetchPolicy: "network-only",
      });
      const resources = data?.GetResourcesIncludingPOAndPMByProjectId || [];
      cacheOperations.writeResourcesIncludingPOAndPMByProjectId(
        projectId,
        resources,
        projectManagerOnly,
        projectManagerOnly ? [] : variables.departments || []
      );
      return { success: true, data: resources };
    } catch (error) {
      console.error("Error fetching GetResourcesIncludingPOAndPMByProjectId:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getProjectResorceMappings,
    getProjectResorceMappingsByProjectId,
    getProjectResorceMappingById,
    createProjectResorceMapping,
    updateProjectResorceMapping,
    deleteProjectResorceMapping,
    cacheOperations,
    getProjectResourcesByProjectId,
    getResourcesByProjectId,
    getResourcesIncludingPOAndPMByProjectId,
  };
};
